import { createAsyncThunk } from '@reduxjs/toolkit';
import { RootReducerState } from '../../redux/Reducers';
import axios from 'axios';
import { rejectWithApiError } from '../../utils/api';
import { PaginationType } from '../../types/utils/pagination.type';
import { RouteParamsType } from '../../types/utils/router-params.type';
import { additionalQueryParams } from '../../utils/utilities';
import { setModalContent, setShowModal } from '../utils/utils.slice';
import {
  CreateFiberCircuitDto,
  FiberCircuitListItemType,
  FiberCircuitType,
  UpdateFiberCircuitDto,
} from '../../types/fiber/fiber.types';

export const postFiberCircuitDetails = createAsyncThunk<
  FiberCircuitType,
  { data: CreateFiberCircuitDto },
  { state: RootReducerState }
>('fiber/postFiberCircuitDetails', async ({ data }, thunkAPI) => {
  try {
    const response = await axios.post<FiberCircuitType>(`${process.env.REACT_APP_API_URL}/fiber-circuit`, data);
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during creating fiber circuit!' });
  }
});

export const getFiberCircuitsList = createAsyncThunk<
  PaginationType<FiberCircuitListItemType>,
  RouteParamsType & { controller?: AbortController },
  { state: RootReducerState }
>('fiber/getFiberCircuitsList/no-loader', async (data, thunkAPI) => {
  try {
    const { controller, ...params } = data;
    const additionalParams = additionalQueryParams(
      thunkAPI.getState().sharedReducer.selectedCompany,
      thunkAPI.getState().sharedReducer.selectedLocation,
      thunkAPI.getState().sharedReducer.includeSuborgs,
      thunkAPI.getState().sharedReducer.selectedCountry
    );
    const mergedParams = { ...params, ...additionalParams };
    const queryString = Object.keys(mergedParams)
      .map(key => key + '=' + mergedParams[key])
      .join('&');

    const response = await axios.get<PaginationType<FiberCircuitListItemType>>(
      `${process.env.REACT_APP_API_URL}/fiber-circuit${queryString ? `?${queryString}` : ''}`,
      controller ? { signal: controller.signal } : undefined
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching fiber circuits!' });
  }
});

export const getFiberCircuitDetails = createAsyncThunk<FiberCircuitType, { id: number }, { state: RootReducerState }>(
  'fiber/getFiberCircuitDetails',
  async ({ id }, thunkAPI) => {
    try {
      const response = await axios.get<FiberCircuitType>(`${process.env.REACT_APP_API_URL}/fiber-circuit/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching fiber circuit!' });
    }
  }
);

export const patchFiberCircuitDetails = createAsyncThunk<
  FiberCircuitType,
  { id: number; data: UpdateFiberCircuitDto },
  { state: RootReducerState }
>('fiber/patchFiberCircuitDetails', async ({ data, id }, thunkAPI) => {
  try {
    const response = await axios.patch<FiberCircuitType>(`${process.env.REACT_APP_API_URL}/fiber-circuit/${id}`, data);
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating fiber circuit!' });
  }
});

export const deleteFiberCircuitDetails = createAsyncThunk<
  FiberCircuitType,
  { id: number; fetchList: boolean },
  { state: RootReducerState }
>('fiber/deleteFiberCircuitDetails', async ({ id, fetchList }, thunkAPI) => {
  try {
    const response = await axios.delete<FiberCircuitType>(`${process.env.REACT_APP_API_URL}/fiber-circuit/${id}`);
    if (fetchList) thunkAPI.dispatch(getFiberCircuitsList(thunkAPI.getState().fiberReducer.fiberCircuitsRouteParams));
    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during deleting fiber circuit!' });
  }
});
